import { CaretDown, List, X, ArrowRight } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { PLATFORM_ITEMS, SCOUT_ITEM, SOLUTION_ITEMS, RESOURCE_ITEMS } from "@/data/siteNav";
import { cn } from "@/lib/utils";
import { BOOKING_URL, LOGIN_URL, signupUrl } from "@/lib/links";

/**
 * Site navigation for the 2026 redesign.
 *
 * Desktop: the floating pill. Platform, Solutions and Resources are
 * disclosure menus: click (or Enter/Space) toggles one open, Escape or a click
 * outside closes it, and focus leaving the menu closes it too. Company is a
 * plain link. On the right: "Sign in", "Book a demo" (secondary), "Sign up"
 * (primary).
 * Mobile: a sticky bar with an accordion of the same structure.
 *
 * Everything listed comes from src/data/siteNav.ts — do not add links here.
 */

type TopLevel = "platform" | "solutions" | "resources";

const TOP_LEVEL: Array<{
  key: TopLevel | "company";
  label: string;
  href?: string;
}> = [
  { key: "platform", label: "Platform" },
  { key: "solutions", label: "Solutions" },
  { key: "resources", label: "Resources" },
  { key: "company", label: "Company", href: "/company" },
];

const menuItemClass =
  "group/item flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-primary-200/50 focus-visible:bg-primary-200/50";

function PlatformMenu({ onNavigate }: { onNavigate?: () => void }) {
  const Scout = SCOUT_ITEM.icon;
  return (
    <div className="grid md:grid-cols-[2fr_1fr]">
      <div className="grid gap-1 p-4 sm:grid-cols-2">
        {PLATFORM_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.slug} to={item.href} className={menuItemClass} onClick={onNavigate}>
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-200 text-primary-600">
                <Icon size={18} weight="bold" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{item.label}</span>
                <span className="block text-xs text-muted-foreground">{item.short}</span>
              </span>
            </Link>
          );
        })}
      </div>
      <div className="flex flex-col gap-3 border-t border-rule bg-mono-100/40 p-5 md:border-l md:border-t-0">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          Scout
        </p>
        <Link to={SCOUT_ITEM.href} className="flex items-center gap-3" onClick={onNavigate}>
          <span className="flex size-8 items-center justify-center rounded-full bg-ink text-indigo-300">
            <Scout size={16} weight="bold" />
          </span>
          <span className="text-sm font-semibold text-foreground">Meet Scout</span>
        </Link>
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          The investigation partner across every capability. It shows its evidence and waits
          for your call.
        </p>
        <div className="mt-auto flex flex-col gap-2 border-t border-rule pt-4">
          <Link
            to="/platform"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground hover:text-primary-600"
            onClick={onNavigate}
          >
            Platform overview <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/integrations"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground hover:text-primary-600"
            onClick={onNavigate}
          >
            Integrations <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function SolutionsMenu({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="grid gap-1 p-4">
      {SOLUTION_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <Link key={item.slug} to={item.href} className={menuItemClass} onClick={onNavigate}>
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-200 text-primary-600">
              <Icon size={18} weight="bold" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-foreground">{item.label}</span>
              <span className="block text-xs text-muted-foreground">{item.short}</span>
            </span>
          </Link>
        );
      })}
      <Link
        to="/been-breached"
        className={cn(menuItemClass, "mt-1 border-t border-rule pt-4")}
        onClick={onNavigate}
      >
        <span>
          <span className="block text-sm font-semibold text-foreground">Been breached?</span>
          <span className="block text-xs text-muted-foreground">
            Start scoping now, while help spins up.
          </span>
        </span>
      </Link>
    </div>
  );
}

function ResourcesMenu({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="grid gap-0.5 p-3">
      {RESOURCE_ITEMS.map((item) =>
        item.external ? (
          <a
            key={item.label}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-primary-200/50"
            onClick={onNavigate}
          >
            {item.label}
          </a>
        ) : (
          <Link
            key={item.label}
            to={item.href}
            className="rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-primary-200/50"
            onClick={onNavigate}
          >
            {item.label}
          </Link>
        )
      )}
    </div>
  );
}

const MENUS: Record<
  TopLevel,
  { width: string; render: (p: { onNavigate?: () => void }) => React.ReactNode }
> = {
  platform: { width: "w-[760px]", render: PlatformMenu },
  solutions: { width: "w-[380px]", render: SolutionsMenu },
  resources: { width: "w-[220px]", render: ResourcesMenu },
};

function DesktopItem({
  item,
  open,
  onToggle,
  onClose,
}: {
  item: (typeof TOP_LEVEL)[number];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const { pathname } = useLocation();
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const active =
    (item.key === "platform" && pathname.startsWith("/platform")) ||
    (item.key === "solutions" && pathname.startsWith("/solutions")) ||
    (item.href && pathname === item.href);
  const linkClass = cn(
    "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:text-primary-600",
    active || open ? "text-primary-600" : "text-foreground"
  );

  // While open: Escape closes and returns focus to the button; focus moving
  // anywhere outside the button and its menu closes it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      onClose();
      button.current?.focus();
    };
    const onFocusIn = (e: FocusEvent) => {
      if (!wrap.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open, onClose]);

  if (item.href) {
    return (
      <Link to={item.href} className={linkClass}>
        {item.label}
      </Link>
    );
  }

  const menu = MENUS[item.key as TopLevel];
  const Menu = menu.render;
  const menuId = `nav-menu-${item.key}`;
  return (
    <div ref={wrap} className="relative">
      <button
        ref={button}
        type="button"
        className={linkClass}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={onToggle}
      >
        {item.label}
        <CaretDown
          size={12}
          weight="bold"
          aria-hidden
          className={cn("text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div
          id={menuId}
          className={cn("absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3", menu.width)}
        >
          <div className="overflow-hidden rounded-2xl border border-rule bg-card shadow-[0_24px_60px_-20px_rgba(20,17,43,0.25)]">
            <Menu onNavigate={onClose} />
          </div>
        </div>
      )}
    </div>
  );
}

function MobileSection({
  label,
  children,
  open,
  onToggle,
}: {
  label: string;
  children: React.ReactNode;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-rule">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-3 text-sm font-semibold text-foreground"
      >
        {label}
        <CaretDown
          size={14}
          weight="bold"
          className={cn("text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>
      {open && <div className="-mx-3 pb-2">{children}</div>}
    </div>
  );
}

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<TopLevel | null>(null);
  const [openMenu, setOpenMenu] = useState<TopLevel | null>(null);
  const desktop = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // Close every menu on navigation.
  useEffect(() => {
    setIsMenuOpen(false);
    setOpenSection(null);
    setOpenMenu(null);
  }, [pathname]);

  // A click anywhere outside the desktop menus closes the open one.
  useEffect(() => {
    if (!openMenu) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!desktop.current?.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openMenu]);

  // Escape closes the mobile menu too.
  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isMenuOpen]);

  const close = () => setIsMenuOpen(false);
  const toggleSection = (key: TopLevel) => setOpenSection((s) => (s === key ? null : key));

  return (
    <nav
      data-cta-placement="navbar"
      className="sticky top-0 isolate z-50 border-b border-rule bg-background/85 px-6 py-3 backdrop-blur-xl md:fixed md:top-8 md:left-1/2 md:w-full md:max-w-6xl md:-translate-x-1/2 md:rounded-2xl md:border md:shadow-[0_1px_2px_rgba(20,17,43,0.04),0_12px_32px_-16px_rgba(20,17,43,0.18)]"
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-between gap-6">
        <Link
          to="/"
          aria-label="Go to homepage"
          className="shrink-0 transition-opacity duration-200 hover:opacity-80"
        >
          <img
            src="/assets/logos/huntbase-logo-dark.svg"
            alt="Huntbase"
            width={120}
            height={32}
            className="h-7 w-auto"
          />
        </Link>

        {/* Desktop */}
        <div ref={desktop} className="hidden flex-1 items-center justify-center gap-1 md:flex">
          {TOP_LEVEL.map((item) => (
            <DesktopItem
              key={item.key}
              item={item}
              open={openMenu === item.key}
              onToggle={() =>
                setOpenMenu((m) => (m === item.key ? null : (item.key as TopLevel)))
              }
              onClose={() => setOpenMenu(null)}
            />
          ))}
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full px-3 py-2 text-sm font-medium text-foreground transition-colors hover:text-primary-600"
          >
            Sign in
          </a>
          <Link
            to={BOOKING_URL}
            className="hidden rounded-full border border-rule px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary-500/40 hover:text-primary-600 lg:inline-flex"
          >
            Book a demo
          </Link>
          <a
            href={signupUrl("navbar")}
            className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Sign up
          </a>
        </div>

        <button
          type="button"
          className="flex size-10 items-center justify-center text-foreground transition-colors hover:text-primary-600 md:hidden"
          onClick={() => setIsMenuOpen((v) => !v)}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          {isMenuOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
        </button>
      </div>

      {/* Mobile */}
      {isMenuOpen && (
        <div
          id="mobile-menu"
          className="mt-3 max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-rule pt-1 md:hidden"
        >
          <MobileSection
            label="Platform"
            open={openSection === "platform"}
            onToggle={() => toggleSection("platform")}
          >
            <PlatformMenu onNavigate={close} />
          </MobileSection>
          <MobileSection
            label="Solutions"
            open={openSection === "solutions"}
            onToggle={() => toggleSection("solutions")}
          >
            <SolutionsMenu onNavigate={close} />
          </MobileSection>
          <MobileSection
            label="Resources"
            open={openSection === "resources"}
            onToggle={() => toggleSection("resources")}
          >
            <ResourcesMenu onNavigate={close} />
          </MobileSection>
          <Link
            to="/company"
            className="block border-b border-rule py-3 text-sm font-semibold"
            onClick={close}
          >
            Company
          </Link>
          <div className="flex flex-col gap-3 py-4">
            <a
              href={signupUrl("navbar-mobile")}
              className="w-full rounded-full bg-primary-500 px-6 py-3 text-center text-sm font-semibold text-white"
              onClick={close}
            >
              Sign up
            </a>
            <Link
              to={BOOKING_URL}
              className="w-full rounded-full border border-rule px-6 py-3 text-center text-sm font-semibold text-foreground"
              onClick={close}
            >
              Book a demo
            </Link>
            <a
              href={LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2 text-center text-sm font-medium text-foreground"
              onClick={close}
            >
              Sign in
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
